import { useMemo } from 'react';

import type { DayResult, Job } from '../../../src/types.ts';
import { dayCheapest, priceBands, type MaxStops } from '../lib/flights.ts';
import { fmtMoney, WEEKDAYS } from '../lib/format.ts';

interface Props {
  job: Job;
  maxStops: MaxStops;
  onOpenDay: (day: DayResult) => void;
}

export function CalendarGrid({ job, maxStops, onOpenDay }: Props) {
  const cheapestPerDay = useMemo(() => job.days.map((d) => dayCheapest(d, maxStops)), [job, maxStops]);
  const bands = useMemo(() => priceBands(cheapestPerDay.flatMap((c) => (c ? [c.price] : []))), [cheapestPerDay]);

  const firstDate = job.days[0]?.date;
  const startWeekday = firstDate ? new Date(`${firstDate}T00:00:00Z`).getUTCDay() : 0;

  return (
    <div className="calendar" role="grid" aria-label="Cheapest fare per day">
      {WEEKDAYS.map((w) => (
        <div key={w} className="calendar__weekday">
          {w}
        </div>
      ))}
      {Array.from({ length: startWeekday }, (_, i) => (
        <div key={`pad-${i}`} className="day day--empty" aria-hidden="true" />
      ))}
      {job.days.map((day, i) => {
        const cheapest = cheapestPerDay[i] ?? null;
        const classes = ['day'];
        let price: React.ReactNode = '…';
        let disabled = false;

        if (day.status === 'pending' || day.status === 'running') {
          classes.push('day--pending');
          price = day.status === 'pending' ? '—' : '…';
          disabled = true;
        } else if (cheapest) {
          price = (
            <>
              {fmtMoney(cheapest.price)}
              <span className="day__currency">{cheapest.currency}</span>
            </>
          );
          if (cheapest.price === bands.min) classes.push('day--cheapest');
          else if (bands.p25 != null && cheapest.price <= bands.p25) classes.push('day--cheap');
          else if (bands.p75 != null && cheapest.price >= bands.p75) classes.push('day--expensive');
        } else {
          classes.push('day--error');
          price = day.status === 'error' ? 'error' : 'no flights';
          disabled = true;
        }

        return (
          <button
            key={day.date}
            type="button"
            className={classes.join(' ')}
            disabled={disabled}
            onClick={() => onOpenDay(day)}
            title={day.date}
          >
            <span className="day__num">{Number(day.date.slice(-2))}</span>
            <span className="day__price">{price}</span>
          </button>
        );
      })}
    </div>
  );
}
